import { produce } from 'immer';
import { omit } from 'lodash/fp';
import type { ViewUid } from 'app/lib/types/server';
import {
    LIST_PROJECT_HISTORY_ACTION,
    LIST_VIEW_HISTORY_ACTION,
    TOGGLE_DIALOG
} from './actionTypes';
import type { ProjectHistoryOutcomes } from './actions';
import type { ProjectHistoryEntry, ViewHistoryEntry, ProjectHistoryFlow } from './types';

export interface AugmentedProjectHistoryFlow extends ProjectHistoryFlow {
    originNode: ViewUid | null;
}

export interface ProjectHistorySlice {
    entries: Array<Omit<ProjectHistoryEntry, 'flow'>>;
    flowByVersion: Record<number, AugmentedProjectHistoryFlow>;
    isLoading: boolean;
    isDialogOpen: boolean;
    currentViewHistory: { entries: ViewHistoryEntry[]; isLoading: boolean };
}

export const INITIAL_STATE: ProjectHistorySlice = {
    entries: [],
    flowByVersion: {},
    isLoading: false,
    isDialogOpen: false,
    currentViewHistory: {
        entries: [],
        isLoading: false
    }
};

export const projectHistoryReducer = produce((draft, action: ProjectHistoryOutcomes) => {
    switch (action.type) {
        case LIST_PROJECT_HISTORY_ACTION.STARTED:
            draft.entries = [];
            draft.flowByVersion = {};
            draft.isLoading = true;
            break;

        case LIST_PROJECT_HISTORY_ACTION.SUCCEEDED: {
            const { projectUid } = action.params;
            const currentNodes = action.payload.entries[0]?.flow.nodes ?? {};

            for (const entry of action.payload.entries) {
                const { flow, ...rest } = entry;
                draft.entries.push(rest);

                const nodes = { ...flow.nodes };

                // Decorate Nodes with exists flag to determine if the node exists
                // in the most recent version
                for (const nodeId in nodes) {
                    nodes[nodeId]!.exists = currentNodes.hasOwnProperty(nodeId);
                }

                for (const group of flow.groups) {
                    const uid = `_g${group.id}`;
                    const uuid = `${projectUid}._g${group.id}`;
                    const status = group.children.some((child) => nodes[child]?.status === 'error')
                        ? 'error'
                        : 'ok';

                    nodes[uid] = { uid, uuid, status, type: 'GROUP', name: group.name };
                }

                draft.flowByVersion[entry.version] = {
                    originNode: entry.origin_node,
                    nodes,
                    groups: flow.groups,
                    edges: flow.edges
                };
            }
            break;
        }

        case LIST_PROJECT_HISTORY_ACTION.ENDED:
            draft.isLoading = false;
            break;
        case LIST_VIEW_HISTORY_ACTION.STARTED: {
            draft.currentViewHistory.isLoading = true;
            break;
        }
        case LIST_VIEW_HISTORY_ACTION.SUCCEEDED: {
            const entries = action.payload.entries;
            draft.currentViewHistory.entries = entries.map((entry) => {
                const { view, ...restEntry } = entry || {};
                const data = view?.payload || view?.transformations;
                let configuration;
                try {
                    configuration = JSON.stringify(omit('_type', data), null, 4);
                } catch {
                    configuration = null;
                }

                return {
                    ...restEntry,
                    configuration
                };
            });
            break;
        }
        case LIST_VIEW_HISTORY_ACTION.ENDED: {
            draft.currentViewHistory.isLoading = false;
            break;
        }
        case TOGGLE_DIALOG:
            draft.isDialogOpen = !draft.isDialogOpen;
            break;

        default:
            break;
    }
}, INITIAL_STATE);
